import React, { FunctionComponent, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'
import moment from 'moment'

import {
  skipFeedReview,
  reserveReview,
  setIsReserved,
} from 'Redux/actions/feedActions'
import { getFeedDetails } from 'Redux/selectors/feed'
import { Mobile, Desktop } from 'Shared/components/Responsive'
import arrowLeftIcon from 'Shared/icons/arrow-left.svg'
import { ReviewStatus } from 'Types/index'

import { ModalProps } from './props'
import {
  ModalContainer,
  MobileModalContainer,
  HeaderContainer,
  HeaderTitle,
  HeaderTitleAlt,
  BackButton,
  ModalTitle,
  ReservedContainer,
  ReservedText,
  ButtonsContainer,
  Button,
  ButtonSimple,
  TimeLeft,
} from './styles'

export const Modal: FunctionComponent<ModalProps> = ({
  title,
  isModalVisible,
  modalHeaderGreen,
  isActualCampaignDetailsLoaded,
  setIsActualCampaignDetailsLoaded,
  closeModal,
  onActionCallback,
  children,
}) => {
  const dispatch = useDispatch()
  const feedDetails = useSelector(getFeedDetails)

  const [isUserHere, setIsUserHere] = useState(true)
  const [timerStartTime, setTimerStartTime] = useState(new Date().getTime())
  const [isTimerColorRed, setIsTimerColorRed] = useState(false)
  const [isTimerSeparator, setIsTimerSeparator] = useState(false)
  const [isSkipDisabled, setIsSkipDisabled] = useState(false)

  const restartTimer = () => {
    setTimerStartTime(new Date().getTime())
    setIsTimerColorRed(false)
  }

  const handleUserIsHere = () => {
    dispatch(
      reserveReview({
        campaign_id: feedDetails.campaign_id,
        // eslint-disable-next-line no-console
        callback: error => console.log('error: ', error),
      })
    )
    setIsUserHere(true)
    restartTimer()
  }

  useEffect(() => {
    const timerInterval = setInterval(() => {
      // Starting countdown timer when modal opens if campaign has not awaiting_review status
      if (
        !isActualCampaignDetailsLoaded ||
        feedDetails.review_status !== ReviewStatus.awaiting_review ||
        feedDetails.isReserved
      ) {
        clearInterval(timerInterval)
        return
      }

      // Function to format minutes and seconds to 2 digits
      const formatTimeLeftParts = (timeLeftPart: number): string =>
        timeLeftPart.toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false,
        })

      setIsTimerSeparator(true)
      const display = document.querySelector('#modalTitle') // Get element for displaying time left
      const displayAlt = document.querySelector('#modalTitleAlt') // Get element for displaying time for mobile when its only 1 minute left
      const finish = moment(timerStartTime).add('minutes', 15) // Calculate countdown finish time
      const diff = Math.abs(moment().diff(finish)) // Calculate difference between finish time and current time
      const timeLeft = moment.duration(diff) // Get time left
      // Show time left
      if (display) {
        display.innerHTML = `${formatTimeLeftParts(
          timeLeft.minutes()
        )}:${formatTimeLeftParts(timeLeft.seconds())}`
        // If time left less than a minute - turn on warning
        if (timeLeft.minutes() < 1 && isUserHere) {
          setIsTimerColorRed(true)
          isUserHere && setIsUserHere(false)
          // If time left less than a minute on mobile - show time left in mobile warning
          if (displayAlt) {
            displayAlt.textContent = `${formatTimeLeftParts(
              timeLeft.minutes()
            )}:${formatTimeLeftParts(timeLeft.seconds())}`
          }
        }
        // If there is no time left - stop and hide timer
        if (timeLeft.minutes() === 0 && timeLeft.seconds() === 0) {
          clearInterval(timerInterval)
          if (display) display.textContent = ``
          if (displayAlt) displayAlt.textContent = ``
          setIsTimerSeparator(false)
          setIsUserHere(true)
          dispatch(setIsReserved())
        }
      }
    }, 1000)
    return () => {
      clearInterval(timerInterval)
    }
  }, [timerStartTime, isActualCampaignDetailsLoaded])

  useEffect(() => {
    const shouldTimerStart =
      !feedDetails?.isReserved &&
      feedDetails.review_status === ReviewStatus.awaiting_review
    if (shouldTimerStart) {
      restartTimer()
    }
  }, [feedDetails])

  const skipReview = () => {
    setIsSkipDisabled(true)
    dispatch(
      skipFeedReview({
        campaign_id: feedDetails.campaign_id,
        callback: error => {
          if (error) {
            // eslint-disable-next-line no-console
            console.log('error: ', error)
          }
          setIsSkipDisabled(false)
          onActionCallback()
          closeModal()
        },
      })
    )
  }

  const [debouncedSkipReview] = useDebouncedCallback(skipReview, 1000)

  const handleCloseModal = () => {
    closeModal()
    if (feedDetails.isReserved) {
      onActionCallback()
    }
  }

  return (
    <>
      <Desktop>
        <ModalContainer
          title={
            <ModalTitle modalHeaderGreen={modalHeaderGreen}>
              {`${title}${isTimerSeparator ? ' - ' : ''}`}
              <TimeLeft red={isTimerColorRed} id="modalTitle" />
            </ModalTitle>
          }
          visible={isModalVisible}
          modalHeaderGreen={modalHeaderGreen}
          onCancel={handleCloseModal}
        >
          {children}
          {!isUserHere && (
            <ReservedContainer>
              <ReservedText>
                Are you still here and want to finish review ?
              </ReservedText>
              <ButtonsContainer>
                <ButtonSimple
                  disabled={isSkipDisabled}
                  onClick={debouncedSkipReview}
                >
                  No, I want to skip review
                </ButtonSimple>
                <Button onClick={handleUserIsHere}>
                  Yes, I want to finish review
                </Button>
              </ButtonsContainer>
            </ReservedContainer>
          )}
        </ModalContainer>
      </Desktop>
      <Mobile>
        <HeaderContainer>
          <BackButton src={arrowLeftIcon} onClick={closeModal} />
          <HeaderTitle>
            {`${title}${isTimerSeparator ? ' - ' : ''}`}
            <TimeLeft red={isTimerColorRed} id="modalTitle" />
          </HeaderTitle>
        </HeaderContainer>
        <MobileModalContainer>
          {children}
          {!isUserHere && (
            <ReservedContainer>
              <HeaderTitleAlt>
                {`${title}${isTimerSeparator ? ' - ' : ''}`}
                <TimeLeft red={isTimerColorRed} id="modalTitleAlt" />
              </HeaderTitleAlt>
              <ReservedText>
                Are you still here and want to finish review ?
              </ReservedText>
              <ButtonsContainer>
                <ButtonSimple
                  reserved
                  disabled={isSkipDisabled}
                  onClick={debouncedSkipReview}
                >
                  No, I want to skip review
                </ButtonSimple>
                <Button onClick={handleUserIsHere}>
                  Yes, I want to finish review
                </Button>
              </ButtonsContainer>
            </ReservedContainer>
          )}
        </MobileModalContainer>
      </Mobile>
    </>
  )
}
